import React from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import * as Table from "ds/Table";
import Tag from "ds/Tag";
import { capitalize } from "Libs/utils";
import { UserWithAccesses } from "Reducers/access/environmentTypeAccess/environmentTypeAccess.types";

import * as S from "./ProjectAccessList.styles";

import type { Invitation } from "@packages/client";

const sortEnvironmentTypes = (envA, envB) => {
  let typeA = envA[0];
  if (envA[1].type) {
    typeA = envA[1].type;
  }

  let typeB = envB[0];
  if (envB[1].type) {
    typeB = envB[1].type;
  }

  const order = {
    production: 0,
    staging: 1,
    development: 2
  };

  return order[typeA] - order[typeB];
};

export const AccessTable = ({
  invitations,
  users
}: {
  invitations: Invitation[];
  users: Record<string, UserWithAccesses>;
}) => {
  const intl = useIntl();
  const [scrollPosition, onScroll] = Table.useScrollPosition();
  const [sort, setSort] = Table.useSort<{ name: string; email: string }>({
    key: "email",
    direction: "asc"
  });
  const accesses = [
    // we add a fake name here so invitations are always sorted above
    // or below users (that will be ordered by name)
    ...invitations.map(i => ({ type: "invitation", name: "", ...i })),
    ...Object.values(users).map(u => ({ type: "user", ...u }))
  ].filter((access, _, array) => {
    const allAccessesWithThisEmail = array.filter(
      a => a.email === access.email
    );
    if (allAccessesWithThisEmail.length > 1) {
      return access.type === "user";
    }
    return true;
  });

  const sortedAccesses = Object.values(accesses).sort((a, b) => {
    if (sort.key === "name") {
      if (a.name < b.name) {
        return sort.direction === "asc" ? -1 : 1;
      }

      if (a.name > b.name) {
        return sort.direction === "asc" ? 1 : -1;
      }

      return 0;
    }

    if (a.email < b.email) {
      return sort.direction === "asc" ? -1 : 1;
    }

    if (a.email > b.email) {
      return sort.direction === "asc" ? 1 : -1;
    }

    return 0;
  });

  return (
    <Table.TableFlex>
      <Table.Thead hasBorderBottom={scrollPosition > 0}>
        <Table.Th
          width="20%"
          onSortChanged={() => {
            setSort("name");
          }}
          isSortActive={sort.key === "name"}
          sortDirection={sort.direction}
        >
          {intl.formatMessage({ id: "user_name" })}
        </Table.Th>
        <Table.Th
          width="20%"
          onSortChanged={() => {
            setSort("email");
          }}
          isSortActive={sort.key === "email"}
          sortDirection={sort.direction}
        >
          {intl.formatMessage({ id: "email" })}
        </Table.Th>
      </Table.Thead>
      <Table.Tbody onScroll={onScroll}>
        {sortedAccesses.map(access => {
          // Deals with the edge case of users with a project role of viewer
          // and no access to any environment type
          if (access.role === "viewer" && !access.environments) {
            return null;
          }

          return (
            <Link
              key={access.email}
              to={
                access.type === "invitation"
                  ? `invitations/${access.id}`
                  : `users/${access.id}`
              }
              style={{ textDecoration: "none" }}
            >
              <Table.Tr>
                <Table.Td width="20%">
                  {access.type === "invitation" ? (
                    <Tag variant="gray">
                      {intl.formatMessage({
                        id: "invitation.pending"
                      })}
                    </Tag>
                  ) : (
                    <Typography.BodyText size="small" weight="semiBold">
                      {access.name}
                    </Typography.BodyText>
                  )}
                </Table.Td>
                <Table.Td width="35%">
                  {access.type === "invitation" ? (
                    <S.GraniteText size="small">{access.email}</S.GraniteText>
                  ) : (
                    access.email
                  )}
                </Table.Td>
                {access.role === "admin" ? (
                  <Table.Td width="45%">
                    <S.Column>
                      <div>
                        {access.type === "invitation" ? (
                          <S.GraniteText size="small">
                            {intl.formatMessage({
                              id: "invitation.table.admin"
                            })}
                          </S.GraniteText>
                        ) : (
                          intl.formatMessage({ id: "invitation.table.admin" })
                        )}
                      </div>
                      <S.GraniteSmallText weight="semiBold">
                        {intl.formatMessage({ id: "invitation.table.project" })}
                      </S.GraniteSmallText>
                    </S.Column>
                  </Table.Td>
                ) : (
                  Object.entries(access.environments)
                    .sort(sortEnvironmentTypes)
                    .map(env => {
                      const role = env[1].role;
                      let type = env[0];
                      if (env[1].type) {
                        type = env[1].type;
                      }
                      return (
                        <Table.Td width="15%" key={`${access.id}-${type}`}>
                          <S.Column>
                            <div>
                              {access.type === "invitation" ? (
                                <S.GraniteText size="small">
                                  {capitalize(
                                    intl.formatMessage({
                                      id: `access.options.${role}`
                                    })
                                  )}
                                </S.GraniteText>
                              ) : (
                                <Typography.BodyText size="small">
                                  {capitalize(
                                    intl.formatMessage({
                                      id: `access.options.${role}`
                                    })
                                  )}
                                </Typography.BodyText>
                              )}
                            </div>
                            <S.GraniteSmallText weight="semiBold">
                              {capitalize(
                                intl.formatMessage({
                                  id: `access.options.${type}`
                                })
                              )}
                            </S.GraniteSmallText>
                          </S.Column>
                        </Table.Td>
                      );
                    })
                )}
              </Table.Tr>
            </Link>
          );
        })}
      </Table.Tbody>
    </Table.TableFlex>
  );
};
